/**
 * Safe DOM manipulation utilities to prevent React DOM errors
 */

/**
 * Safely removes a child node from its parent
 * @param {Node} parent - The parent node
 * @param {Node} child - The child node to remove
 * @returns {boolean} - True if removal was successful, false otherwise
 */
export const safeRemoveChild = (parent, child) => {
    try {
        // Check if parent and child exist
        if (!parent || !child) {
            console.warn('safeRemoveChild: Parent or child node is null/undefined');
            return false;
        }

        // Check if child is actually a child of parent
        if (!parent.contains(child)) {
            console.warn('safeRemoveChild: Child is not a child of parent node');
            return false;
        }

        // Check if parent still has the child (double-check)
        if (parent.contains(child)) {
            parent.removeChild(child);
            return true;
        }

        return false;
    } catch (error) {
        console.error('safeRemoveChild: Error removing child node:', error);
        return false;
    }
};

/**
 * Safely removes a child node using modern remove() method
 * @param {Node} child - The child node to remove
 * @returns {boolean} - True if removal was successful, false otherwise
 */
export const safeRemove = (child) => {
    try {
        if (!child) {
            console.warn('safeRemove: Child node is null/undefined');
            return false;
        }

        // Check if the node is still in the DOM
        if (child.parentNode) {
            child.remove();
            return true;
        }

        return false;
    } catch (error) {
        console.error('safeRemove: Error removing node:', error);
        return false;
    }
};

/**
 * Safely appends a child to a parent node
 * @param {Node} parent - The parent node
 * @param {Node} child - The child node to append
 * @returns {boolean} - True if append was successful, false otherwise
 */
export const safeAppendChild = (parent, child) => {
    try {
        if (!parent || !child) {
            console.warn('safeAppendChild: Parent or child node is null/undefined');
            return false;
        }

        // Check if child is already a child of parent
        if (parent.contains(child)) {
            console.warn('safeAppendChild: Child is already a child of parent');
            return true; // Already there, consider it successful
        }

        parent.appendChild(child);
        return true;
    } catch (error) {
        console.error('safeAppendChild: Error appending child node:', error);
        return false;
    }
};

/**
 * Safely inserts a node before another node
 * @param {Node} parent - The parent node
 * @param {Node} newNode - The new node to insert
 * @param {Node} referenceNode - The reference node to insert before
 * @returns {boolean} - True if insert was successful, false otherwise
 */
export const safeInsertBefore = (parent, newNode, referenceNode) => {
    try {
        if (!parent || !newNode) {
            console.warn('safeInsertBefore: Parent or newNode is null/undefined');
            return false;
        }

        // Check if referenceNode is actually a child of parent
        if (referenceNode && !parent.contains(referenceNode)) {
            console.warn('safeInsertBefore: Reference node is not a child of parent');
            // Fallback to appendChild
            return safeAppendChild(parent, newNode);
        }

        if (referenceNode) {
            parent.insertBefore(newNode, referenceNode);
        } else {
            parent.appendChild(newNode);
        }
        return true;
    } catch (error) {
        console.error('safeInsertBefore: Error inserting node:', error);
        // Fallback to appendChild
        return safeAppendChild(parent, newNode);
    }
};

/**
 * Checks if a node is still in the DOM
 * @param {Node} node - The node to check
 * @returns {boolean} - True if node is in DOM, false otherwise
 */
export const isNodeInDOM = (node) => {
    try {
        if (!node) return false;
        return document.contains(node);
    } catch (error) {
        console.error('isNodeInDOM: Error checking node:', error);
        return false;
    }
};

/**
 * Safely cleans up DOM nodes (useful for component unmounting)
 * @param {Node[]} nodes - Array of nodes to clean up
 * @returns {number} - Number of nodes successfully removed
 */
export const safeCleanupNodes = (nodes) => {
    let removedCount = 0;
    
    if (!Array.isArray(nodes)) {
        console.warn('safeCleanupNodes: Expected array of nodes');
        return 0;
    }

    nodes.forEach(node => {
        if (node && isNodeInDOM(node)) {
            if (safeRemove(node)) {
                removedCount++;
            }
        }
    });

    return removedCount;
};

export default {
    safeRemoveChild,
    safeRemove,
    safeAppendChild,
    safeInsertBefore,
    isNodeInDOM,
    safeCleanupNodes
};
